// Escalations — its own page again (previously a section inside the now-retired Live
// Status page). Derived live from groups.escalation_flag + sentiment_history; see
// buildEscalations() in lib/queueMetrics.js and docs/backend-integration.md item 3 for
// what a real persisted record would add.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MessageCircle,
  Bot,
  Clock,
  UserCog,
  MessageSquareQuote,
  History,
  Users,
  ClipboardList,
  UserPlus,
  CircleCheck,
  ShieldAlert,
  X,
} from "lucide-react";
import { NUM, CARD, ESCALATION_STATUS, ESCALATION_SOURCE_LABEL, formatWaited, formatWhen } from "../lib/dashboardTokens";
import { useDemoIdentity } from "../lib/DemoIdentityContext";
import { useGroups, useAllSentimentHistory, useAllStaffToneFlags, useConfig } from "../lib/api";
import { buildEscalations, scopeByWorkshop, filterByLocationParam } from "../lib/queueMetrics";
import {
  rangeFromSearchParams,
  formatRangeChipLabel,
  filterRowsByCreatedRange,
  searchParamsWithoutLocation,
  searchParamsWithoutRange,
} from "../lib/dates";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { TablePagination } from "../components/TablePagination";

const SOURCE_ICON = { classifier: Bot, sla_breach: Clock, manual: UserCog, staff_tone: ShieldAlert };
// Positive/Neutral/Negative are customer-sentiment timeline entries; high/medium
// are a staff-tone flag's own risk_level, reused here as the same field.
const SENTIMENT_DOT = {
  Positive: "bg-emerald-500",
  Neutral: "bg-slate-400",
  Negative: "bg-red-500",
  high: "bg-red-500",
  medium: "bg-amber-500",
};
const PAGE_SIZE = 10;

// Who an escalation can be assigned to: the management chain for that specific workshop
// (Stakeholders page's config table), not a free-text name — GM/WM/CCM are the roles that
// actually own resolving an escalation, PSF/Receptionist don't.
const ASSIGNABLE_ROLES = [
  { role: "GM", nameKey: "GM Name" },
  { role: "WM", nameKey: "WM Name" },
  { role: "CCM", nameKey: "CCM Name" },
];

function assignableFor(configRows, workshopLocation) {
  const cfg = configRows.find((c) => c["Workshop Location"] === workshopLocation);
  if (!cfg) return [];
  return ASSIGNABLE_ROLES.map((r) => ({ role: r.role, name: cfg[r.nameKey] })).filter((r) => r.name);
}

function StatusBadge({ status }) {
  const t = ESCALATION_STATUS[status];
  if (!t) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium ${t.text} ${t.bg} ${t.border}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
      {t.label}
    </span>
  );
}

function SourceBadge({ source }) {
  const Icon = SOURCE_ICON[source] || UserCog;
  const label = ESCALATION_SOURCE_LABEL[source] || source;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
      <Icon size={12} strokeWidth={2.2} />
      {label}
    </span>
  );
}

export default function Escalations() {
  const navigate = useNavigate();
  const identity = useDemoIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationFilter = searchParams.get("location");
  const dateRange = useMemo(() => rangeFromSearchParams(searchParams), [searchParams]);
  const dateLabel = dateRange ? formatRangeChipLabel(dateRange) : null;
  const { data: groupsData, isLoading: groupsLoading } = useGroups();
  const { data: shData, isLoading: sentimentLoading } = useAllSentimentHistory();
  const { data: stData, isLoading: staffToneLoading } = useAllStaffToneFlags();
  const { data: cfgData } = useConfig();
  const loading = groupsLoading || sentimentLoading || staffToneLoading;

  const scopedGroups = useMemo(() => {
    const rows = groupsData?.rows || [];
    return dateRange ? filterRowsByCreatedRange(rows, dateRange) : rows;
  }, [groupsData, dateRange]);

  const baseEscalations = useMemo(
    () => buildEscalations(scopedGroups, shData?.rows || [], stData?.rows || []),
    [scopedGroups, shData, stData],
  );
  const configRows = cfgData?.rows || [];

  // Assign / close have nowhere to persist yet — there's no dedicated escalations table
  // row behind these incidents. This overlay keeps the actions usable in this session
  // without pretending they're saved anywhere.
  const [overrides, setOverrides] = useState({});
  const [activity, setActivity] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [ownerDraft, setOwnerDraft] = useState("");

  const escalations = useMemo(() => baseEscalations.map((e) => ({ ...e, ...(overrides[e.id] || {}) })), [baseEscalations, overrides]);

  const scoped = useMemo(() => {
    const list = filterByLocationParam(scopeByWorkshop(escalations, identity), locationFilter);
    return [...list].sort((a, b) => {
      if (a.status !== b.status) return a.status === "open" ? -1 : 1;
      return new Date(b.raisedAt).getTime() - new Date(a.raisedAt).getTime();
    });
  }, [escalations, identity, locationFilter]);

  const openCount = scoped.filter((e) => e.status === "open").length;
  const closedCount = scoped.filter((e) => e.status === "closed").length;

  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [scoped.length, identity, locationFilter, dateLabel]);
  const pageRows = scoped.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const selected = useMemo(() => escalations.find((e) => e.id === selectedId) || null, [escalations, selectedId]);
  const timeline = selected?.timeline || [];
  const notified = []; // notifications table has no way to link back to these incidents yet
  const log = selectedId ? activity[selectedId] || [] : [];
  const assignOptions = selected ? assignableFor(configRows, selected.workshopLocation) : [];

  function openDrawer(esc) {
    setSelectedId(esc.id);
    setOwnerDraft("");
  }

  function updateEscalation(id, patch) {
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  function appendActivity(id, kind, text) {
    setActivity((prev) => ({ ...prev, [id]: [...(prev[id] || []), { kind, text, at: new Date().toISOString() }] }));
  }

  function handleAssign(esc) {
    if (!ownerDraft) return;
    const [role, name] = ownerDraft.split("|");
    updateEscalation(esc.id, { ownerName: name, ownerRole: role });
    appendActivity(esc.id, "assign", `Assigned to ${name} (${role})`);
  }

  function handleClose(esc) {
    if (esc.status === "closed") return;
    updateEscalation(esc.id, { status: "closed", closedAt: new Date().toISOString() });
    appendActivity(esc.id, "close", "Closed");
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="escalations-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-work text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Escalations</h1>
        <div className="flex flex-wrap items-center gap-2" data-testid="escalations-summary">
          {locationFilter && (
            <button
              type="button"
              onClick={() => setSearchParams(searchParamsWithoutLocation(searchParams))}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
              data-testid="location-filter-chip"
            >
              {locationFilter}
              <X size={12} />
            </button>
          )}
          {dateLabel && (
            <button
              type="button"
              onClick={() => setSearchParams(searchParamsWithoutRange(searchParams))}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
              data-testid="date-filter-chip"
            >
              {dateLabel}
              <X size={12} />
            </button>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            <span className={NUM}>{openCount}</span> open
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span className={NUM}>{closedCount}</span> closed
          </span>
        </div>
      </div>

      <div className={CARD} data-testid="escalations-table-card">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="escalations-loading">
            Loading…
          </div>
        ) : scoped.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500" data-testid="escalations-empty">
            No escalations in view for this role and workshop.
          </div>
        ) : (
          <>
            <Table data-testid="escalations-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Workshop</TableHead>
                  <TableHead>Advisor</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Raised</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer" data-testid={`escalation-row-${e.id}`} onClick={() => openDrawer(e)}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{e.customerName}</div>
                      <div className="text-xs text-slate-500">{e.vehicleReg}</div>
                    </TableCell>
                    <TableCell className="text-slate-700">{e.workshopLocation}</TableCell>
                    <TableCell className="text-slate-700">{e.advisorName}</TableCell>
                    <TableCell className="max-w-sm text-slate-800">{e.reasonText}</TableCell>
                    <TableCell className={`whitespace-nowrap text-slate-600 ${NUM}`}>{formatWhen(e.raisedAt)}</TableCell>
                    <TableCell>
                      <StatusBadge status={e.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={page} pageSize={PAGE_SIZE} total={scoped.length} onPageChange={setPage} />
          </>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="escalation-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-work">{selected.customerName}</SheetTitle>
                <SheetDescription>
                  {selected.vehicleReg} · {selected.workshopLocation} · {selected.advisorName}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <SourceBadge source={selected.source} />
                  <StatusBadge status={selected.status} />
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/journey/${selected.groupRowId}`)} data-testid="go-to-chat-btn">
                  <MessageCircle size={14} /> Go to chat
                </Button>
              </div>

              <div className="mt-5 text-sm text-slate-800">{selected.reasonText}</div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-500">
                <div>
                  <div className="text-slate-400">Raised</div>
                  <div className={`text-slate-700 ${NUM}`}>{formatWhen(selected.raisedAt)}</div>
                </div>
                <div>
                  <div className="text-slate-400">{selected.status === "open" ? "Open for" : "Closed"}</div>
                  <div className={`text-slate-700 ${NUM}`}>
                    {selected.status === "open"
                      ? formatWaited((Date.now() - new Date(selected.raisedAt).getTime()) / 60000)
                      : formatWhen(selected.closedAt)}
                  </div>
                </div>
              </div>

              {selected.triggeringMessage && (
                <div className="mt-6">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <MessageSquareQuote size={13} /> Triggering message
                  </div>
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 italic">
                    “{selected.triggeringMessage}”
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <History size={13} /> Sentiment timeline
                </div>
                {timeline.length === 0 ? (
                  <div className="mt-2 text-sm text-slate-400" data-testid="timeline-empty">
                    No sentiment history recorded for this escalation.
                  </div>
                ) : (
                  <div className="mt-3 space-y-4">
                    {timeline.map((t, i) => (
                      <div key={i} className="relative flex gap-3">
                        <div className="flex flex-col items-center">
                          <span className={`h-2.5 w-2.5 rounded-full shrink-0 mt-1 ${SENTIMENT_DOT[t.sentiment] || "bg-slate-400"}`} />
                          {i !== timeline.length - 1 && <div className="w-px flex-1 mt-1 bg-slate-200" />}
                        </div>
                        <div className="pb-1 min-w-0">
                          <div className="text-sm text-slate-800">{t.note}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {t.sentiment} · {formatWhen(t.at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Users size={13} /> Owner
                </div>
                <div className="mt-2 text-sm">
                  {selected.ownerName ? (
                    <span className="text-slate-800">
                      {selected.ownerName} <span className="text-slate-400">({selected.ownerRole})</span>
                    </span>
                  ) : (
                    <span className="text-slate-400 italic">Unassigned</span>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Users size={13} /> Notified
                </div>
                {notified.length === 0 ? (
                  <div className="mt-2 text-sm text-slate-400" data-testid="notified-empty">
                    No stakeholders notified yet.
                  </div>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {notified.map((n, i) => (
                      <li key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">
                          {n.name} <span className="text-slate-400">({n.role})</span>
                        </span>
                        <span className={`text-xs text-slate-400 ${NUM}`}>{formatWhen(n.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-6 border-t border-slate-200 pt-5">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <ClipboardList size={13} /> Actions
                </div>
                <p className="mt-1.5 text-xs text-slate-400">
                  Not saved yet — there's no escalation record to save these to. See docs/backend-integration.md.
                </p>

                <div className="mt-3 flex items-center gap-2">
                  <select
                    value={ownerDraft}
                    onChange={(ev) => setOwnerDraft(ev.target.value)}
                    disabled={assignOptions.length === 0}
                    className="h-8 flex-1 min-w-0 rounded-md border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:bg-slate-50 disabled:text-slate-400"
                    data-testid="assign-select"
                  >
                    <option value="">{assignOptions.length ? "Assign to…" : "No GM/WM/CCM on file for this workshop"}</option>
                    {assignOptions.map((o) => (
                      <option key={o.role} value={`${o.role}|${o.name}`}>
                        {o.name} ({o.role})
                      </option>
                    ))}
                  </select>
                  <Button size="sm" variant="outline" disabled={!ownerDraft} onClick={() => handleAssign(selected)} data-testid="action-assign">
                    <UserPlus size={14} /> Assign
                  </Button>
                </div>

                <div className="mt-3">
                  <Button size="sm" variant="outline" disabled={selected.status === "closed"} onClick={() => handleClose(selected)} data-testid="action-close">
                    <CircleCheck size={14} /> Close
                  </Button>
                </div>

                {log.length > 0 && (
                  <ul className="mt-4 space-y-1.5 border-t border-slate-100 pt-3" data-testid="action-log">
                    {log.map((entry, i) => (
                      <li key={i} className="text-xs text-slate-500">
                        <span className="font-medium text-slate-700">{entry.text}</span> · <span className={NUM}>{formatWhen(entry.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
